import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";

import video from "../assets/hero.mp4";
import video2 from "../assets/hero2.mp4";

import london from "../assets/trips/london.jpg";
import bicester from "../assets/trips/bicester.jpg";
import countryside from "../assets/trips/countryside.jpg";
import safari from "../assets/trips/safari.jpg";
import themepark from "../assets/trips/themepark.jpg";
import hunting from "../assets/trips/hunting.jpg";

import airport from "../assets/services/airport.jpg";
import chauffeur from "../assets/services/chauffeur.jpg";
import tours from "../assets/services/tours.jpg";
import fullday from "../assets/services/fullday.jpg";
import { FaWhatsapp } from "react-icons/fa";

function Home() {

  /* ================= TRIPS SLIDER LOGIC ================= */
  const tripsRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = () => {
    const el = tripsRef.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;

    const left = el.scrollLeft > 2;
    const right = el.scrollLeft < max - 2;

    setCanLeft(left);
    setCanRight(right);
  };

  const scroll = (dir) => {
    const el = tripsRef.current;
    if (!el) return;

    el.scrollBy({
      left: dir === "left" ? -420 : 420,
      behavior: "smooth",
    });

    // ensure arrows update after animation
    setTimeout(updateArrows, 300);
  };

  /* ================= DRAG (DESKTOP) ================= */
  const handleMouseDown = (e) => {
    const el = tripsRef.current;
    if (!el) return;

    el.isDown = true;
    el.startX = e.pageX;
    el.scrollStart = el.scrollLeft;
  };

  const handleMouseMove = (e) => {
    const el = tripsRef.current;
    if (!el || !el.isDown) return;

    const walk = e.pageX - el.startX;
    el.scrollLeft = el.scrollStart - walk;
  };

  const stopDrag = () => {
    const el = tripsRef.current;
    if (!el) return;

    el.isDown = false;
    updateArrows(); // IMPORTANT for mobile + desktop sync
  };

  /* ================= MOBILE + SCROLL FIX ================= */
  useEffect(() => {
    const el = tripsRef.current;
    if (!el) return;

    const handleScroll = () => updateArrows();

    el.addEventListener("scroll", handleScroll);
    el.addEventListener("touchend", handleScroll);
    el.addEventListener("mouseup", handleScroll);

    updateArrows(); // initial state

    return () => {
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("touchend", handleScroll);
      el.removeEventListener("mouseup", handleScroll);
    };
  }, []);

  return (
    <div className="home-page">

      {/* ================= HERO ================= */}
      <section className="hero-video">

        <video autoPlay loop muted playsInline className="hero-video-bg">
          <source src={video} type="video/mp4" />
        </video>

        <div className="hero-luxury-overlay"></div>

        <motion.div
          className="hero-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
        >

          <motion.h1
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.6 }}
          >
            Travel in Luxury & Arrive in Style
          </motion.h1>

          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.6, delay: 0.3 }}
          >
            Premium chauffeur services in London for comfort, elegance, and refined travel experiences
          </motion.p>

          <motion.a
            href="https://wa.me/447826699446?text=Hello%2C%20I%20am%20interested%20in%20your%20luxury%20chauffeur%20services"
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 }}
          >
            <button className="hero-btn">Book Now</button>
          </motion.a>

        </motion.div>
      </section>

      {/* ================= MISSION ================= */}
      <section className="mission-section">

        <video className="mission-video" autoPlay loop muted playsInline>
          <source src={video2} type="video/mp4" />
        </video>

        <div className="mission-overlay"></div>
        <div className="mission-glow"></div>

        <motion.div
          className="mission-content"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
        >
          <span className="mission-tag">OUR MISSION</span>

          <h2>
            Redefining Luxury <br /> Chauffeuring in London
          </h2>

          <p className="mission-p">
            We deliver a seamless and first-class travel experience.
            Every journey is designed around comfort, elegance, and absolute reliability —
            from pickup to destination.
          </p>
        </motion.div>

      </section>

      {/* ================= SERVICES ================= */}
      <section className="home-services-ultra">

        <motion.div
          className="services-header-ultra"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <span>SERVICES</span>
          <h2>Our Premium Services</h2>
          <p>
            Tailored chauffeur solutions designed for comfort, elegance, and complete peace of mind.
          </p>
        </motion.div>

        <div className="services-scroll">
          <motion.div
            className="services-track"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >

            <div className="ultra-card">
              <img src={airport} alt="" />
              <div className="ultra-card-overlay"></div>
              <div className="ultra-card-content">
                <h3>Airport Transfers</h3>
                <p>Seamless arrivals with meet & greet service.</p>
              </div>
            </div>

            <div className="ultra-card">
              <img src={chauffeur} alt="" />
              <div className="ultra-card-overlay"></div>
              <div className="ultra-card-content">
                <h3>Luxury Chauffeur</h3>
                <p>Premium vehicles with professional drivers.</p>
              </div>
            </div>

            <div className="ultra-card">
              <img src={tours} alt="" />
              <div className="ultra-card-overlay"></div>
              <div className="ultra-card-content">
                <h3>Private Tours</h3>
                <p>Explore the UK with bespoke journeys.</p>
              </div>
            </div>

            <div className="ultra-card">
              <img src={fullday} alt="" />
              <div className="ultra-card-overlay"></div>
              <div className="ultra-card-content">
                <h3>Full-Day Hire</h3>
                <p>Flexible chauffeur service all day.</p>
              </div>
            </div>

          </motion.div>
        </div>

        <div className="services-btn-wrap">
          <Link to="/services">
            <button className="services-btn-simple">Explore All Services</button>
          </Link>
        </div>

      </section>

      {/* ================= TRIPS ================= */}
      <section className="home-trips">

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >

          <span className="trips-tag">EXPERIENCES</span>

          <h2>Our Signature Journeys</h2>

          <p className="trips-sub">
            Discover handpicked luxury experiences across the UK, designed for comfort, elegance, and unforgettable moments.
          </p>

          {/* LEFT ARROW */}
          <button
            className={`arrow left ${!canLeft ? "disabled" : ""}`}
            onClick={() => scroll("left")}
            disabled={!canLeft}
          >
            ‹
          </button>

          {/* SCROLL */}
          <div
            className="home-trips-scroll"
            ref={tripsRef}
            onScroll={updateArrows}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >

            <motion.div className="home-trip-card">
              <img src={london} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>London Private Tour</h3></div>
            </motion.div>

            <motion.div className="home-trip-card">
              <img src={bicester} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>Bicester Luxury Shopping</h3></div>
            </motion.div>

            <motion.div className="home-trip-card">
              <img src={countryside} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>Countryside Escape</h3></div>
            </motion.div>

            <motion.div className="home-trip-card">
              <img src={safari} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>Wildlife adventure</h3></div>
            </motion.div>

            <motion.div className="home-trip-card">
              <img src={themepark} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>UK Theme Parks</h3></div>
            </motion.div>

            <motion.div className="home-trip-card">
              <img src={hunting} alt="" />
              <div className="trip-overlay"></div>
              <div className="trip-info"><h3>Hunting Experience</h3></div>
            </motion.div>

          </div>

          {/* RIGHT ARROW */}
          <button
            className={`arrow right ${!canRight ? "disabled" : ""}`}
            onClick={() => scroll("right")}
            disabled={!canRight}
          >
            ›
          </button>

          <div className="trips-btn-wrap">
            <Link to="/trips">
              <button className="trips-btn">Explore All Trips</button>
            </Link>
          </div>

        </motion.div>

      </section>

      {/* ================= WHY ================= */}
      <section className="why-section">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <span className="why-tag">EXCELLENCE</span>
          <h2>Why Choose Us</h2>

          <div className="why-stack">
            <div className="why-item">
              <h3>Licensed Professionals</h3>
              <p>Experienced chauffeurs ensuring safety and professionalism.</p>
            </div>

            <div className="why-item">
              <h3>Luxury Vehicles</h3>
              <p>Modern fleet designed for maximum comfort.</p>
            </div>

            <div className="why-item">
              <h3>Punctual & Reliable</h3>
              <p>We respect your time with consistent service.</p>
            </div>

            <div className="why-item">
              <h3>Privacy Guaranteed</h3>
              <p>Discreet travel for VIP clients.</p>
            </div>

            <div className="why-item">
              <h3>Perfect for Everyone</h3>
              <p>Tourists, families, business travellers.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ================= CTA ================= */}
      <section className="cta">

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
        >

          <h2 className="cta-title">Start Your Journey Today</h2>

          <p>
            Experience premium chauffeur service designed for comfort and elegance.
          </p>

          <a
            href="https://wa.me/447826699446?text=Hello%2C%20I%20am%20interested%20in%20your%20luxury%20chauffeur%20services"
            target="_blank"
            rel="noreferrer"
          >
            <button className="cta-btn">
              <FaWhatsapp className="cta-whatsapp-icon" />
              <span>Contact via WhatsApp</span>
            </button>
          </a>

        </motion.div>

      </section>

    </div>
  );
}

export default Home;